import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { Check, ChevronLeft, LocateFixed, RotateCw, Users, ZoomIn, ZoomOut } from "lucide-react";
import {
  TILE_CATALOG,
  claimOptionsForPlacement,
  coordinateKey,
  type ClaimOption,
  type LegalPlacement,
  type LunaView,
  type PlacedTile,
  type Rotation,
  type TileId,
} from "@arcade/luna-genesis";
import { connectRoom, getArcade, roomParticipants } from "../../lib/arcade";
import type { GameTableProps, MatchSummaryPatch } from "../game-core";

const TILE_SIZE = 74;
const FALLBACK_REFRESH_MS = 5 * 60 * 1000;
const PLAYER_COLORS = ["#69d9ff", "#ffce61", "#ff7891", "#9ee36f", "#c79cff"];

type Participant = { playerId: string; displayName: string; avatarHash: string | null };
type Point = { x: number; y: number };

export function LunaGenesisTable({ roomId, roomToken, myName, onExit, onProgress }: GameTableProps) {
  const [view, setView] = useState<LunaView | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [rotation, setRotation] = useState<Rotation>(0);
  const [selected, setSelected] = useState<LegalPlacement | null>(null);
  const [claim, setClaim] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);

  const refresh = useCallback(async () => {
    const arcade = await getArcade("luna-genesis", myName);
    setView(await arcade.getLunaGenesisState(roomId, roomToken) as LunaView);
  }, [myName, roomId, roomToken]);

  useEffect(() => {
    let cancelled = false;
    void refresh()
      .catch((cause) => { if (!cancelled) setError(messageFor(cause, "Could not reach the lunar table.")); });
    return () => { cancelled = true; };
  }, [myName, refresh, roomId, roomToken]);

  useEffect(() => {
    if (!view) return;
    let cancelled = false;
    void roomParticipants("luna-genesis", roomId, roomToken, myName)
      .then((result) => { if (!cancelled) setParticipants(result.participants); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [myName, roomId, roomToken, view?.players.length]);

  useEffect(() => {
    let closed = false;
    let closeSocket: (() => void) | undefined;
    void connectRoom("luna-genesis", roomId, roomToken).then((room) => {
      if (closed) return room.close();
      const unsubscribe = room.on("state", () => void refresh().catch(() => undefined));
      closeSocket = () => { unsubscribe(); room.close(); };
    }).catch(() => undefined);
    return () => { closed = true; closeSocket?.(); };
  }, [refresh, roomId, roomToken]);

  useEffect(() => {
    if (view?.phase === "complete") return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh().catch(() => undefined);
    }, FALLBACK_REFRESH_MS);
    const foreground = () => { if (document.visibilityState === "visible") void refresh().catch(() => undefined); };
    document.addEventListener("visibilitychange", foreground);
    window.addEventListener("focus", foreground);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", foreground);
      window.removeEventListener("focus", foreground);
    };
  }, [refresh, view?.phase]);

  useEffect(() => {
    if (!view || !onProgress) return;
    onProgress(summaryFor(view));
  }, [onProgress, view]);

  const isMyTurn = view?.phase === "playing" && view.activePlayerId === view.you;
  const legalAtRotation = useMemo(() => view?.legalPlacements.filter((placement) => placement.rotation === rotation) ?? [], [rotation, view]);
  const claimOptions = useMemo<ClaimOption[]>(() => {
    if (!view?.currentTile || !selected) return [];
    return claimOptionsForPlacement(view, view.currentTile, selected);
  }, [selected, view]);
  const names = useMemo(() => new Map(participants.map((participant) => [participant.playerId, participant.displayName])), [participants]);

  useEffect(() => {
    setSelected(null);
    setClaim(null);
  }, [rotation, view?.revision]);

  const commit = async () => {
    if (!view || !selected || !isMyTurn) return;
    setBusy(true); setError("");
    try {
      const arcade = await getArcade("luna-genesis", myName);
      const next = await arcade.submitLunaGenesisCommand(roomId, roomToken, {
        type: "commit_turn",
        commandId: crypto.randomUUID(),
        expectedRevision: view.revision,
        placement: selected,
        ...(claim ? { claimRegionId: claim } : {}),
      }) as LunaView;
      setView(next);
      setRotation(0);
    } catch (cause) {
      setError(messageFor(cause, "That landing could not be confirmed."));
      // A timed-out command may still have reached the authoritative room. Release the controls
      // immediately and reconcile in the background instead of extending "Confirming" through a
      // second network request.
      void refresh().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const resign = async () => {
    if (!view || !confirm("Resign this Luna Genesis match?")) return;
    setBusy(true); setError("");
    try {
      const arcade = await getArcade("luna-genesis", myName);
      setView(await arcade.submitLunaGenesisCommand(roomId, roomToken, {
        type: "resign", commandId: crypto.randomUUID(), expectedRevision: view.revision,
      }) as LunaView);
    } catch (cause) { setError(messageFor(cause, "Could not resign.")); }
    finally { setBusy(false); }
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) pinch.current = { distance: pointerDistance(pointers.current), zoom };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 1) setPan((current) => ({ x: current.x + event.clientX - previous.x, y: current.y + event.clientY - previous.y }));
    else if (pointers.current.size === 2 && pinch.current) setZoom(clamp(pinch.current.zoom * pointerDistance(pointers.current) / pinch.current.distance, 0.65, 1.8));
  };
  const endPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  if (!view) return <article className="luna-table luna-loading"><button className="icon-button" onClick={onExit} aria-label="Back to games"><ChevronLeft /></button><div className="luna-orbit-loader" /><p>Establishing lunar link…</p>{error && <p className="form-error">{error}</p>}</article>;
  const waiting = view.phase === "waiting_for_players";
  const status = waiting ? `Waiting for ${view.capacity - view.players.length} more` : view.phase === "complete" ? resultLabel(view) : isMyTurn ? "Your expedition" : `${names.get(view.activePlayerId ?? "") ?? "Another explorer"} is placing`;

  return <article className="luna-table">
    <header className="luna-header">
      <button className="icon-button" onClick={onExit} aria-label="Back to games"><ChevronLeft /></button>
      <div><p className="eyebrow">Luna Genesis</p><h1>{status}</h1></div>
      <span className="luna-tile-count"><strong>{view.tilesRemaining}</strong><small>tiles</small></span>
    </header>

    <PlayerRail view={view} names={names} />

    {waiting ? <section className="luna-waiting">
      <div className="luna-moon"><span /></div><Users />
      <h2>{view.players.length} of {view.capacity} explorers ready</h2>
      <p>Share the game code with every player. The server shuffles the expedition when the final seat joins.</p>
    </section> : <>
      <section
        className="luna-board-viewport"
        aria-label="Luna Genesis tile board. Drag to pan and pinch to zoom."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <div className="luna-board-world" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
          {Object.values(view.board).map((tile) => <BoardTile key={coordinateKey(tile.x, tile.y)} tile={tile} claims={view.claims} players={view.players.map((player) => player.playerId)} />)}
          {isMyTurn && legalAtRotation.map((placement) => {
            const key = coordinateKey(placement.x, placement.y);
            const chosen = selected?.x === placement.x && selected.y === placement.y;
            return <button
              key={`${key}:${rotation}`}
              className={`luna-placement${chosen ? " selected" : ""}`}
              style={positionStyle(placement.x, placement.y)}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => setSelected(placement)}
              aria-label={`Place tile at ${placement.x}, ${placement.y}`}
            >{chosen && view.currentTile ? <TileSprite tileId={view.currentTile} rotation={rotation} /> : <span>+</span>}</button>;
          })}
        </div>
        <div className="luna-map-controls" aria-label="Map controls">
          <button onClick={() => setZoom((value) => clamp(value - 0.15, 0.65, 1.8))} aria-label="Zoom out"><ZoomOut /></button>
          <button onClick={() => { setPan({ x: 0, y: 0 }); setZoom(1); }} aria-label="Center board"><LocateFixed /></button>
          <button onClick={() => setZoom((value) => clamp(value + 0.15, 0.65, 1.8))} aria-label="Zoom in"><ZoomIn /></button>
        </div>
      </section>

      <section className="luna-action-dock" aria-live="polite">
        {view.phase === "complete" ? <EndPanel view={view} names={names} /> : !isMyTurn ? <>
          <p className="luna-action-kicker">Expedition in progress</p>
          <strong>We’ll alert you when the turn comes home.</strong>
          {view.deadline && <small>Turn deadline {new Date(view.deadline).toLocaleString()}</small>}
        </> : <>
          <div className="luna-drawn-tile">
            {view.currentTile && <TileSprite tileId={view.currentTile} rotation={rotation} />}
            <div><p className="luna-action-kicker">Current tile</p><strong>{tileName(view.currentTile)}</strong><small>{selected ? `Landing ${selected.x}, ${selected.y}` : `${legalAtRotation.length} highlighted landings`}</small></div>
            <button className="luna-rotate" onClick={() => setRotation(nextRotation(rotation))} disabled={busy} aria-label="Rotate tile clockwise"><RotateCw /><span>Rotate</span></button>
          </div>
          {selected && <fieldset className="luna-claim-options">
            <legend>Place a colonist?</legend>
            <button className={claim === null ? "active" : ""} onClick={() => setClaim(null)}>No colonist</button>
            {claimOptions.map((option) => <button key={option.regionId} className={claim === option.regionId ? "active" : ""} disabled={(view.players.find((player) => player.playerId === view.you)?.colonists ?? 0) < 1} onClick={() => setClaim(option.regionId)}>{option.label}</button>)}
          </fieldset>}
          <button className="luna-confirm" disabled={busy || !selected} onClick={() => void commit()}><Check />{busy ? "Confirming…" : selected ? "Confirm landing" : "Choose a landing"}</button>
        </>}
        {view.phase !== "complete" && <button className="luna-resign" disabled={busy} onClick={() => void resign()}>Resign match</button>}
        {error && <p className="form-error" role="alert">{error}</p>}
      </section>
    </>}
  </article>;
}

function PlayerRail({ view, names }: { view: LunaView; names: Map<string, string> }) {
  return <section className="luna-player-rail" aria-label="Player scores">
    {view.players.map((player, index) => <div key={player.playerId} className={`${player.playerId === view.activePlayerId ? "active" : ""}${player.playerId === view.you ? " you" : ""}${player.forfeited ? " forfeited" : ""}`}>
      <i style={{ background: PLAYER_COLORS[index] }} />
      <span><strong>{names.get(player.playerId) ?? (player.playerId === view.you ? "You" : `Explorer ${index + 1}`)}</strong><small>{player.colonists} colonists</small></span>
      <b>{player.score}</b>
    </div>)}
  </section>;
}

function BoardTile({ tile, claims, players }: { tile: PlacedTile; claims: LunaView["claims"]; players: string[] }) {
  const key = coordinateKey(tile.x, tile.y);
  const tileClaims = claims.filter((claim) => claim.node.startsWith(`${key}|`));
  return <div className="luna-board-tile" style={positionStyle(tile.x, tile.y)}>
    <TileSprite tileId={tile.tileId} rotation={tile.rotation} />
    {tileClaims.map((claim, index) => <span key={claim.node} className="luna-colonist" style={{ background: PLAYER_COLORS[Math.max(0, players.indexOf(claim.playerId))], left: `${42 + index * 18}%` }} aria-label="Claimed feature" />)}
  </div>;
}

export function TileSprite({ tileId, rotation = 0 }: { tileId: TileId; rotation?: Rotation }) {
  const index = TILE_CATALOG[tileId].spriteIndex;
  const column = index % 4;
  const row = Math.floor(index / 4);
  return <span className="luna-sprite-frame"><span className="luna-sprite" style={{
    backgroundPosition: `${column * 100 / 3}% ${row * 100 / 3}%`,
    transform: `rotate(${rotation}deg)`,
  }} /></span>;
}

function EndPanel({ view, names }: { view: LunaView; names: Map<string, string> }) {
  return <div className="luna-end-panel"><p className="luna-action-kicker">Expedition complete</p><h2>{resultLabel(view)}</h2><ol>{[...view.players].sort((a, b) => b.score - a.score).map((player) => <li key={player.playerId}><span>{names.get(player.playerId) ?? (player.playerId === view.you ? "You" : "Explorer")}</span><strong>{player.score}</strong></li>)}</ol></div>;
}

function summaryFor(view: LunaView): MatchSummaryPatch {
  const me = view.players.find((player) => player.playerId === view.you);
  const opponentScore = Math.max(0, ...view.players.filter((player) => player.playerId !== view.you).map((player) => player.score));
  if (view.phase !== "complete") return { myScore: me?.score ?? 0, opponentScore, status: view.activePlayerId === view.you ? "your-turn" : "waiting" };
  return {
    myScore: me?.score ?? 0,
    opponentScore,
    status: "finished",
    result: view.winnerIds.includes(view.you) ? (view.winnerIds.length > 1 ? "draw" : "won") : "lost",
  };
}

function resultLabel(view: LunaView): string {
  if (view.winnerIds.length > 1 && view.winnerIds.includes(view.you)) return "Shared lunar victory";
  return view.winnerIds.includes(view.you) ? "You founded the leading colony" : "The expedition is complete";
}
function tileName(tileId: TileId | null): string { return tileId ? tileId.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "No tile"; }
function nextRotation(rotation: Rotation): Rotation { return ((rotation + 90) % 360) as Rotation; }
function clamp(value: number, min: number, max: number): number { return Math.min(max, Math.max(min, value)); }
function pointerDistance(points: Map<number, Point>): number { const [a, b] = [...points.values()]; return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 1; }
function positionStyle(x: number, y: number): CSSProperties { return { left: x * TILE_SIZE - TILE_SIZE / 2, top: y * TILE_SIZE - TILE_SIZE / 2 }; }
function messageFor(cause: unknown, fallback: string): string { return cause instanceof Error ? cause.message.replace(/^arcade \d+: /, "").replaceAll("_", " ") : fallback; }
